/**
 * SeoCompetitorsOrganicPage — `/seo/competitors` (A2, Фаза 2). Органическая
 * видимость конкурентов (SoV) на базе того же списка доменов, что рекламный модуль,
 * но метрика — органический трафик (SpyWords). Cache-first + кнопка «Обновить».
 */

import { usePageHeader } from '@/components/layout/pageHeader';
import { HelpButton } from '@/components/layout/HelpButton';
import { Button, LoadingBlock, ErrorBlock } from '@/shared/ui';
import { useSeoCompetitorsOrganic, useSeoPhase2Mutations } from '@/hooks/api/useSeo';

const HELP = {
  title: 'SEO — Органик-конкуренты',
  why: 'Кто ранжируется по нашим темам в органике и с каким объёмом. Тот же список доменов, что рекламный модуль, но метрика — органический трафик, не расход.',
  tasks: ['Видеть органический SoV конкурентов', 'Сравнивать их органику с рекламой', 'Искать content-gap'],
  how: ['Смотрите таблицу по органическому трафику.', 'Обновите снапшот при необходимости (SpyWords).'],
};

function num(v: number): string {
  return new Intl.NumberFormat('ru-RU').format(v);
}

export function SeoCompetitorsOrganicPage() {
  usePageHeader({ title: 'SEO — Органик-конкуренты', subtitle: 'Органическая видимость (SoV)', actions: <HelpButton help={HELP} /> });
  const { data, isLoading, isError, error, refetch } = useSeoCompetitorsOrganic();
  const { refreshCompetitorsOrganic } = useSeoPhase2Mutations();

  return (
    <div className="col gap20" style={{ maxWidth: 1000 }}>
      <div className="row spread wrap" style={{ gap: 8 }}>
        <span className="muted" style={{ fontSize: 13 }}>
          Снапшот: <span className="mono">{data?.generatedAt ? new Date(data.generatedAt).toLocaleString('ru-RU') : 'нет'}</span>
        </span>
        <Button variant="outline" size="sm" icon="Loader" onClick={() => refreshCompetitorsOrganic.mutate()} disabled={refreshCompetitorsOrganic.isPending}>
          {refreshCompetitorsOrganic.isPending ? 'Собираю…' : 'Обновить'}
        </Button>
      </div>

      {refreshCompetitorsOrganic.isError && <ErrorBlock text={refreshCompetitorsOrganic.error?.message} />}
      {isLoading && <LoadingBlock label="Загружаю…" />}
      {isError && <ErrorBlock text={error?.message} onRetry={() => void refetch()} />}

      {data && !data.configured && (
        <div className="card"><p className="muted" style={{ margin: 0 }}>SpyWords не настроен. Раздел работает в режиме «внешний аудит».</p></div>
      )}

      {data?.configured && (
        data.competitors.length === 0 ? (
          <div className="card"><p className="muted" style={{ margin: 0 }}>Снапшота ещё нет — нажмите «Обновить».</p></div>
        ) : (
          <section className="card">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Конкурент</th>
                  <th>Категория</th>
                  <th style={{ textAlign: 'right' }}>Органик-трафик</th>
                  <th style={{ textAlign: 'right' }}>Рекл. трафик</th>
                </tr>
              </thead>
              <tbody>
                {data.competitors.map((c) => (
                  <tr key={c.domain}>
                    <td>
                      <div className="col" style={{ gap: 2 }}>
                        <span style={{ fontWeight: 600 }}>{c.name}</span>
                        <span className="muted mono" style={{ fontSize: 11.5 }}>{c.domain}</span>
                      </div>
                    </td>
                    <td><span className="muted" style={{ fontSize: 12.5 }}>{c.category}</span></td>
                    <td className="tnum" style={{ textAlign: 'right' }}>{num(c.orgTraf)}</td>
                    <td className="tnum" style={{ textAlign: 'right' }}>{num(c.adTraf)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )
      )}
    </div>
  );
}
